import { useDispatch } from 'react-redux';
import { styled } from '@mui/material';
import { red } from '@mui/material/colors';
import { useSearchParams } from 'react-router-dom';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import AuthInputsContainer from 'components/AuthInputsContainer';
import AuthLink from 'components/AuthLink';
import AuthPage from 'components/AuthPage';
import FormCard from 'components/FormCard';
import Button from 'components/Button';
import SupportButton from 'components/SupportButton';
import { ROUTES } from 'navigation/constants';

import { submitRegistrationForm } from '../actions';
import { initialValues, createRegistrationSchema } from '../form';
import { IRegistrationForm } from '../types';

import EmailField from './EmailField';
import PasswordField from './PasswordField';
import NameField from './NameField';

const Headline = styled('h4')(({ theme }) => ({
  ...theme.typography.h5,
  margin: '0 0 32px',
}));

const ErrorText = styled('div')({
  fontSize: 14,
  fontWeight: 400,
  color: red[500],
});

const Registration = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const name = searchParams.get('name') ?? '';
  const rawEmail = searchParams.get('email') ?? '';
  const email = rawEmail.replace(/ /g, '+');
  const web2wave_user_id = searchParams.get('web2wave_user_id') ?? '';
  const headline = email ? t('auth.setPasswordToFinish') : t('auth.register');

  return (
    <AuthPage>
      <Formik<IRegistrationForm>
        initialValues={{ ...initialValues, email: email ?? '', name: name ?? '', web2wave_user_id: web2wave_user_id ?? '' } as IRegistrationForm}
        validationSchema={createRegistrationSchema(t)}
        onSubmit={({ name, email, password }, actions) => {
          dispatch(submitRegistrationForm({ name, email, password, actions, web2wave_user_id }));
        }}
      >
        {(formikProps: FormikProps<IRegistrationForm>) => {
          const { handleSubmit, handleReset, isSubmitting, errors } = formikProps;

          return (
            <FormCard onSubmit={handleSubmit} onReset={handleReset}>
              <AuthInputsContainer>
                <Headline>{headline}</Headline>
                <NameField name="name" placeholder={t('auth.name')} />
                <EmailField name="email" placeholder={t('auth.email')} disabled={!!email && !errors.email} autoComplete="username" />
                <PasswordField name="password" placeholder={t('auth.password')} autoComplete="new-password" />
                <PasswordField name="confirmPassword" placeholder={t('auth.confirmPassword')} autoComplete="new-password" />
                {!!errors.form && <ErrorText>{errors.form}</ErrorText>}
                <Button text={t('auth.signUp')} isLoading={isSubmitting} />
                <AuthLink
                  mainText={t('auth.haveAccount')}
                  linkText={t('auth.signIn')}
                  link={ROUTES.LOGIN.path}
                />
                <SupportButton />
              </AuthInputsContainer>
            </FormCard>
          );
        }}
      </Formik>
    </AuthPage>
  );
};

export default Registration;
